import LiveApiClient from "../api/LiveApiClient";
import OfflineStore from "./OfflineStore";
import OfflineErrors from "./OfflineErrors";

/**
 * Item_Price_Fetcher — batch-fetches customer prices and caches in IDB.
 *
 * Three-tier cache: memory Map → IDB ("offline:prices") → API.
 *
 * The IDB tier is only consulted when the catalog has offline mode enabled.
 * For online-only catalogs the fetcher uses memory + API; the IDB cache has
 * no per-item TTL and would otherwise serve indefinitely-stale prices when
 * Item.retail_price changes server-side.
 *
 * Usage:
 *   const fetcher = new Item_Price_Fetcher(catalogId, accountCode, apiKey, onDemandConfig, offlineEnabled);
 *   const prices = await fetcher.getForItems([101, 102, 103]);
 *   fetcher.enrichItems(items); // mutates items in-place with cached prices
 */
export default class Item_Price_Fetcher {
	#catalogId;
	#accountCode;
	#apiKey;
	#endpoint;
	#batchSize;
	#fields;
	#offlineEnabled;
	#memCache = new Map(); // itemId → priceData
	#currency = null; // { symbol, label } from Get_Prices response

	/**
	 * @param {string|number} catalogId
	 * @param {string} accountCode
	 * @param {string} apiKey
	 * @param {object} [config] - from on_demand.prices: { endpoint, batch_size }
	 * @param {boolean} [offlineEnabled=false] - whether the catalog has offline mode enabled
	 */
	constructor(catalogId, accountCode, apiKey, config = {}, offlineEnabled = false) {
		this.#catalogId = catalogId;
		this.#accountCode = accountCode;
		this.#apiKey = apiKey;
		this.#endpoint = (config.endpoint || "/Customer_Item/fn/Get_Prices").replace(/^\/api\//, '/');
		this.#batchSize = config.batch_size || 100;
		this.#fields = config.fields || [
			"retail_price", "unit_price", "price_range", "price_updated_on"
		];
		this.#offlineEnabled = !!offlineEnabled;
	}

	get catalogId() { return this.#catalogId; }
	get currency() { return this.#currency; }

	/**
	 * Get prices for a batch of item IDs.
	 * Returns from cache when possible, fetches missing from API.
	 * @param {number[]} itemIds
	 * @returns {Promise<Object>} { itemId: { retail_price, unit_price, ... }, ... }
	 */
	async getForItems(itemIds) {
		if (!itemIds?.length) return {};

		const result = {};
		const uncachedIds = [];

		// 1. Check memory cache
		for (const id of itemIds) {
			const cached = this.#memCache.get(id);
			if (cached) {
				result[id] = cached;
			} else {
				uncachedIds.push(id);
			}
		}

		if (uncachedIds.length === 0) return result;

		// 2. Check IDB cache — only when offline mode is enabled for this catalog.
		// Online-only catalogs skip the IDB tier: it has no per-item TTL, so a
		// stale entry would otherwise mask later Item.retail_price changes.
		const store = this.#offlineEnabled ? OfflineStore.ForCatalog(this.#catalogId) : null;
		const idbMissIds = [];
		if (store) {
			const idbData = await store.GetCachedOnDemandData("prices");
			for (const id of uncachedIds) {
				const cached = idbData?.[id];
				if (cached) {
					result[id] = cached;
					this.#memCache.set(id, cached);
				} else {
					idbMissIds.push(id);
				}
			}
		} else {
			idbMissIds.push(...uncachedIds);
		}

		if (idbMissIds.length === 0) return result;

		// 3. Fetch from API in batches (skip when simulating offline — POST would be rejected)
		if (OfflineStore.IsSimulatingOffline()) return result;
		const newEntries = {};
		for (let i = 0; i < idbMissIds.length; i += this.#batchSize) {
			const batch = idbMissIds.slice(i, i + this.#batchSize);
			try {
				const response = await LiveApiClient.post(this.#endpoint,
					{ items: batch, limit: batch.length },
					{
						headers: {
							account: this.#accountCode,
							'api-key': this.#apiKey,
						},
					},
				);
				// Get_Prices response shape: { result: { result: [...records], currency: {...} } }
				// Also accept legacy shapes: { result: [...records] } and a bare array.
				const payload = response.data?.result;
				const records = Array.isArray(response.data)
					? response.data
					: Array.isArray(payload)
						? payload
						: payload?.result;
				const currency = payload?.currency || response.data?.currency;
				if (!this.#currency && currency) {
					this.#currency = currency;
				}
				if (records?.length) {
					for (const record of records) {
						if (!record.item?.id) continue;
						const priceData = {};
						for (const field of this.#fields) {
							if (record[field] !== undefined) {
								priceData[field] = record[field];
							}
						}
						const itemId = +record.item.id;
						result[itemId] = priceData;
						newEntries[itemId] = priceData;
						this.#memCache.set(itemId, priceData);
					}
				}
			} catch (e) {
				console.warn('[Item_Price_Fetcher] Batch fetch failed:', e);
				OfflineErrors.log("Item_Price_Fetcher.getForItems", e);
			}
		}

		// 4. Persist new entries to IDB (merge, don't replace) — only when
		// offline mode is enabled. Skipping the write keeps online-only catalogs
		// from accumulating stale entries that would survive across page loads.
		if (store && Object.keys(newEntries).length > 0) {
			store.MergeOnDemandData("prices", newEntries);
		}

		return result;
	}

	/**
	 * Populate memory cache from IDB for a set of item IDs.
	 * Does not make network calls. Use before enrichItems() for offline use.
	 * No-op for online-only catalogs.
	 * @param {number[]} itemIds
	 */
	async preloadFromCache(itemIds) {
		if (!itemIds?.length || !this.#offlineEnabled) return;

		const store = OfflineStore.ForCatalog(this.#catalogId);
		const idbData = await store.GetCachedOnDemandData("prices");
		if (!idbData) return;

		for (const id of itemIds) {
			if (!this.#memCache.has(id) && idbData[id]) {
				this.#memCache.set(id, idbData[id]);
			}
		}
	}

	/**
	 * Enrich item with cached price data. Mutates in-place.
	 * Only sets fields that are undefined/null on the item.
	 * @param {object} item
	 */
	enrichItem(item) {
		if (!item?.id) return;
		const cached = this.#memCache.get(item.id);
		if (!cached) return;

		for (const [key, value] of Object.entries(cached)) {
			if (item[key] === undefined || item[key] === null) {
				item[key] = value;
			}
		}
	}

	/**
	 * Enrich multiple items with cached price data.
	 * @param {object[]} items
	 */
	enrichItems(items) {
		if (!items?.length) return;
		for (const item of items) {
			this.enrichItem(item);
		}
	}

	/** Clear memory cache */
	clearMemoryCache() {
		this.#memCache.clear();
	}
}
